import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

interface ChartData {
  date: string
  faturamento: number
  vendas: number
}

interface FaturamentoChartProps {
  data: ChartData[]
  loading?: boolean
}

export function FaturamentoChart({ data, loading }: FaturamentoChartProps) {
  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 rounded-lg border border-gray-200 dark:border-slate-700 mt-8">
        <div className="h-80 flex items-center justify-center">
          <p className="text-gray-500 dark:text-gray-400">Carregando gráfico...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-900 p-6 rounded-lg border border-gray-200 dark:border-slate-700 mt-8">
      <div className="mb-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
          Faturamento - Últimos 30 dias
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">Evolução diária do faturamento</p>
      </div>

      <ResponsiveContainer width="100%" height={350}>
        <LineChart
          data={data}
          margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
        >
          <defs>
            <linearGradient id="colorFaturamento" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e7eb"
            className="dark:stroke-slate-700"
          />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            className="dark:fill-gray-400"
            tickFormatter={(date) => {
              const d = new Date(date)
              return `${d.getDate()}/${String(d.getMonth() + 1).padStart(2, '0')}`
            }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#6b7280' }}
            className="dark:fill-gray-400"
            tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip
            formatter={(value: any) => [
              `R$ ${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`,
              'Faturamento'
            ]}
            labelFormatter={(label) => {
              const d = new Date(label)
              return d.toLocaleDateString('pt-BR')
            }}
            contentStyle={{
              backgroundColor: '#1f2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#fff'
            }}
          />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
          />
          <Line
            type="monotone"
            dataKey="faturamento"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ fill: '#3b82f6', r: 4 }}
            activeDot={{ r: 6 }}
            name="Faturamento"
            isAnimationActive={true}
          />
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <p className="text-xs text-blue-600 dark:text-blue-300 mb-1">Maior Faturamento</p>
          <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
            R$ {Math.max(...data.map(d => d.faturamento)).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
          </p>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
          <p className="text-xs text-green-600 dark:text-green-300 mb-1">Média Diária</p>
          <p className="text-2xl font-bold text-green-900 dark:text-green-100">
            R$ {(data.reduce((sum, d) => sum + d.faturamento, 0) / data.length).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
          </p>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
          <p className="text-xs text-purple-600 dark:text-purple-300 mb-1">Total (30 dias)</p>
          <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
            R$ {data.reduce((sum, d) => sum + d.faturamento, 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
          </p>
        </div>
      </div>
    </div>
  )
}
